import { useEffect, useRef, useState } from 'react';
import type { RecentSession } from '../lib/types.js';

type Props = {
  items: RecentSession[];
  onPick: (contentId: string) => void;
  onDelete: (contentId: string) => void;
};

const DELETE_WIDTH = 80;
// Below this drag distance, treat the gesture as a tap and let the click
// through. Above it, the click is suppressed and the drag drives state.
const TAP_THRESHOLD = 5;

export function RecentList({ items, onPick, onDelete }: Props): JSX.Element | null {
  const [openId, setOpenId] = useState<string | null>(null);

  if (items.length === 0) return null;

  return (
    <section className="w-full mt-10">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-secondary text-hint">继续学习</span>
        <span className="flex-1 h-px bg-border" />
      </div>
      <ul className="flex flex-col">
        {items.map((it) => (
          <RecentListItem
            key={it.content_id}
            item={it}
            isOpen={openId === it.content_id}
            onOpen={() => setOpenId(it.content_id)}
            onClose={() => setOpenId((c) => (c === it.content_id ? null : c))}
            onCloseAllOthers={() =>
              setOpenId((c) => (c === it.content_id ? c : null))
            }
            onPick={onPick}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </section>
  );
}

type ItemProps = {
  item: RecentSession;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCloseAllOthers: () => void;
  onPick: (contentId: string) => void;
  onDelete: (contentId: string) => void;
};

function RecentListItem({
  item,
  isOpen,
  onOpen,
  onClose,
  onCloseAllOthers,
  onPick,
  onDelete,
}: ItemProps): JSX.Element {
  const slideRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number | null>(null);
  const baseRef = useRef(0);
  const lastDxRef = useRef(0);
  const wasDraggedRef = useRef(false);

  // Sync visual to the open prop. Animated when state changes (e.g.
  // sibling opening forces us closed) but instant on initial mount.
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;
    el.style.transition = 'transform 200ms ease-out';
    el.style.transform = `translateX(${isOpen ? -DELETE_WIDTH : 0}px)`;
  }, [isOpen]);

  const setTransformImmediate = (x: number) => {
    const el = slideRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = `translateX(${x}px)`;
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    // Mouse right-click / non-primary touches: ignore.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    startXRef.current = e.clientX;
    baseRef.current = isOpen ? -DELETE_WIDTH : 0;
    lastDxRef.current = baseRef.current;
    wasDraggedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (startXRef.current == null) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > TAP_THRESHOLD) wasDraggedRef.current = true;
    // Constrain: can never go past 0 (rightward) or further than ~delete
    // button width × 1.2 (slight rubber-band on overshoot).
    let next = baseRef.current + dx;
    if (next > 0) next = 0;
    if (next < -DELETE_WIDTH * 1.2) next = -DELETE_WIDTH * 1.2;
    lastDxRef.current = next;
    setTransformImmediate(next);
  };

  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = () => {
    if (startXRef.current == null) return;
    startXRef.current = null;
    const final = lastDxRef.current;
    // Snap open if dragged past half the delete width; else snap closed.
    if (final < -DELETE_WIDTH / 2) {
      onOpen();
    } else {
      onClose();
    }
  };

  // Tap on the item content. If we were dragging, swallow the click. If
  // open without drag, close. Otherwise navigate.
  const handleClick = () => {
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    if (isOpen) {
      onClose();
      return;
    }
    onCloseAllOthers();
    onPick(item.content_id);
  };

  return (
    <li className="relative overflow-hidden">
      {/* Delete button sits behind the item, revealed when slid left.
          Inverted (white bg + black text) instead of red — palette is
          locked to black/gray/white per the design tokens. */}
      <button
        type="button"
        aria-label={`删除 ${item.title}`}
        onClick={() => onDelete(item.content_id)}
        className="absolute right-0 top-0 h-full flex items-center justify-center bg-primary text-canvas font-medium text-[14px] active:opacity-80"
        style={{ width: DELETE_WIDTH }}
      >
        删除
      </button>
      <div
        ref={slideRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className="relative bg-canvas touch-pan-y"
      >
        <button
          type="button"
          onClick={handleClick}
          className="w-full flex items-center justify-between py-3 text-left active:bg-surface-2 -mx-4 px-4 rounded-md"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-secondary shrink-0">▸</span>
            <span className="truncate text-primary text-[16px]">{item.title}</span>
          </span>
          <span className="text-secondary text-hint shrink-0 ml-3">{item.pending_count}</span>
        </button>
      </div>
    </li>
  );
}
